/**
 * NovaSimplified Component
 * ======================
 * 
 * A lightweight version of the Nova data explorer component that accepts a DuckDB Engine instance
 * and a Supabase client. This component provides SQL querying, data grid visualization, and AI chat
 * capabilities without the overhead of the full Nova interface.
 * 
 * ## Usage Guide
 * 
 * 1. Prerequisites:
 *    - You must have a DuckDB engine instance already initialized with data tables registered
 *    - You must have a Supabase client initialized
 * 
 * 2. Basic Usage:
 *    ```tsx
 *    import { NovaSimplified } from '@/components/nova/nova-simplified';
 *    import { useEngine } from '@/lib/engine/react';
 *    import { useSupabaseClient } from '@supabase/auth-helpers-react';
 *    
 *    export function MyDataPage() {
 *      // Initialize engine (must be done before using NovaSimplified)
 *      const engine = useEngine();
 *      const supabase = useSupabaseClient();
 *      
 *      return (
 *        <div className="h-screen">
 *          <NovaSimplified 
 *            engine={engine} 
 *            supabase={supabase}
 *            initialQuery="my_table" // Table name/path to load on startup
 *          />
 *        </div>
 *      );
 *    }
 *    ```
 * 
 * 3. Common Pitfalls:
 *    - Engine must be initialized and have tables registered before NovaSimplified renders
 *    - Component requires a fixed height container (add className="h-screen" or similar)
 *    - The initialQuery parameter is the table name/path, not an SQL query
 *    - Tables must be properly registered with the engine using registerTable
 * 
 * 4. Full Example with Table Registration:
 *    ```tsx
 *    import { useEffect, useState } from 'react';
 *    import { NovaSimplified } from '@/components/nova/nova-simplified';
 *    import { useEngine } from '@/lib/engine/react';
 *    import { useSupabaseClient } from '@supabase/auth-helpers-react';
 *    
 *    export function DataExplorerPage() {
 *      const engine = useEngine();
 *      const supabase = useSupabaseClient();
 *      const [isReady, setIsReady] = useState(false);
 *      
 *      useEffect(() => {
 *        // Initialize the engine and register a table
 *        async function setupEngine() {
 *          // Wait for engine to be ready
 *          await engine.waitForInitialization();
 *          
 *          // Register a table from a CSV
 *          const response = await fetch('/data/sample.csv');
 *          const blob = await response.blob();
 *          const tableRef = await engine.registerTable({
 *            id: 'sample-data',
 *            path: 'sample_table', // This is what you'll use in initialQuery
 *            file: blob,
 *            fileType: 'csv'
 *          });
 *          
 *          // Engine is ready with tables
 *          setIsReady(true);
 *        }
 *        
 *        setupEngine().catch(console.error);
 *      }, [engine]);
 *      
 *      if (!isReady) {
 *        return <div>Loading...</div>;
 *      }
 *      
 *      return (
 *        <div className="h-screen">
 *          <NovaSimplified 
 *            engine={engine} 
 *            supabase={supabase}
 *            initialQuery="sample_table"
 *          />
 *        </div>
 *      );
 *    }
 *    ```
 * 
 * 5. Advanced Usage:
 *    - Use the exportButton prop to provide a custom export button
 *    - Use subscriptionInfo and onUpgradeClick for premium features
 *    - Provide initialPrompt to pre-populate the AI chat
 */

import * as React from 'react';
import { cn } from '../../components/ui';
import { DataGrid } from './data-grid/data-grid';
import { ColumnRef, Column } from './data-grid/types';
import { Query, Engine, TableView } from '../../lib/engine';
import { QueryType, SQLDialect } from '../../lib/engine/core/types';
import { NovaContainer } from './nova-container/nova-container';
import { NovaAIChat } from './nova-ai-chat';
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from '../../components/ui/resizable';
import { SubscriptionInfo } from './nova-ai-chat/types';
import { SupabaseClient } from '@supabase/supabase-js';

import { useNovaStore, NovaStore } from './nova/nova-store';
import { NovaStoreProvider, useNovaStoreContext, useNovaEngine } from './nova/nova-store-context';
import { BarChart } from 'lucide-react';

// Helper function to convert ColumnRef to Column
function convertToColumns(columnRefs: ColumnRef[]): Column[] {
  return columnRefs.map(ref => ({
    ...ref.schema,
    name: ref.name,
    header: ref.name,
    sortable: true,
    filterable: true,
    resizable: true
  }));
}

export interface NovaSimplifiedProps {
  /**
   * Engine instance to use for query execution
   */
  engine: Engine;

  /**
   * Supabase instance
   */
  supabase: SupabaseClient;

  /**
   * Optional class name for the container
   */
  className?: string;

  /**
   * URL for the chat endpoint
   * @default '/api/chat'
   */
  chatEndpoint?: string;

  /**
   * Optional content to render in the top-right area of the header
   * This can include export buttons, save buttons, or any other controls
   */
  topRightSlot?: React.ReactNode;

  /**
   * Subscription information for the user
   * Used to control access to AI chat features based on subscription status
   */
  subscriptionInfo?: SubscriptionInfo;

  /**
   * Optional callback when the upgrade link is clicked
   * Used to show the upgrade modal or navigate to upgrade page
   * @param args Object containing the openTrigger source
   */
  onUpgradeClick?: (args: { openTrigger: 'chat_suggestion' | 'upgrade_button' | 'pricing_card' }) => void;

  /**
   * Optional initial prompt to pre-populate the chat input field
   */
  initialPrompt?: string;
  
  /**
   * Optional initial SQL query to execute when the component loads.
   * 
   * IMPORTANT: This should be the table name/path that was used when registering
   * the table with engine.registerTable(). It is NOT an SQL query string.
   * 
   * Example:
   * - Correct: "my_customers"
   * - Incorrect: "SELECT * FROM my_customers"
   * 
   * The component will use this value to locate and display the corresponding
   * table that was previously registered with the engine.
   */
  initialQuery?: string;
}

// Internal interface for NovaContent props
interface NovaSimplifiedContentProps extends NovaSimplifiedProps {
  chatEndpoint: string; // Make this required for internal use
}

/**
 * Simplified Nova Component
 * 
 * A presentational component that renders a Nova container with a DataGrid.
 * This version does not include the dataset list sidebar.
 * 
 * @param props The component props
 * @param props.engine A DuckDB engine instance with tables registered
 * @param props.supabase Initialized Supabase client
 * @param props.initialQuery The table name/path to load initially. This should match 
 *        the 'path' property used when registering the table with engine.registerTable().
 *        NOT an SQL query - just the table identifier. Example: "my_table" not "SELECT * FROM my_table"
 * @param props.className Optional CSS class name for styling
 * @param props.topRightSlot Optional content to render in the top-right area of the header
 * @param props.subscriptionInfo Optional subscription info for gating premium features
 * @param props.onUpgradeClick Optional callback for subscription upgrade flow
 * @param props.initialPrompt Optional pre-populated AI chat message
 * @returns A Nova data explorer component with data grid and AI chat interface
 */
export function NovaSimplified({
  engine,
  supabase,
  className,
  chatEndpoint = '/api/chat',
  topRightSlot,
  subscriptionInfo,
  onUpgradeClick,
  initialPrompt,
  initialQuery
}: NovaSimplifiedProps) {
  return (
    <NovaStoreProvider engine={engine}>
      <NovaSimplifiedContent 
        engine={engine}
        supabase={supabase}
        className={className} 
        chatEndpoint={chatEndpoint} 
        topRightSlot={topRightSlot}
        subscriptionInfo={subscriptionInfo}
        onUpgradeClick={onUpgradeClick}
        initialPrompt={initialPrompt}
        initialQuery={initialQuery}
      />
    </NovaStoreProvider>
  );
}

/**
 * Nova Content Component
 * 
 * Internal component that uses the Nova store context.
 */
function NovaSimplifiedContent({ 
  className, 
  chatEndpoint, 
  topRightSlot,
  subscriptionInfo,
  onUpgradeClick,
  initialPrompt,
  initialQuery,
  supabase
}: NovaSimplifiedContentProps) {
  // Get the engine from the context
  const engine = useNovaEngine();
  
  // Get the Nova store for all state and actions
  const {
    data,
    columns,
    columnRefs,
    originalColumnRefs,
    status,
    error,
    query,
    executeQuery,
    updateQuery,
    setActiveTable,
    activeTable,
    logState,
    lastExecutedQuery,
    lastExecutedView,
    runSqlQuery
  } = useNovaStoreContext();
  
  // State for loading indicator
  const [showLoadingIndicator, setShowLoadingIndicator] = React.useState(false);
  
  // Refs for tracking query execution and table state
  const previousTableRef = React.useRef<string | null>(null);
  
  // Container ref for measuring
  const containerRef = React.useRef<HTMLDivElement>(null);
  
  // Compute derived values and ensure they're safe
  const isLoading = status === 'loading';
  const tableName = activeTable?.path || "Data";
  const safeColumnRefs = columnRefs || [];

  // Handle table changes when initialQuery changes
  React.useEffect(() => {
    const handleTableChange = async () => {
      // Only proceed if we have a new table to set
      if (!initialQuery || initialQuery === previousTableRef.current || isLoading) {
        return;
      }
      
      try {
        console.log(`[NovaSimplified] Setting table from initialQuery: ${initialQuery}`);
        
        // Update ref to prevent infinite loops
        previousTableRef.current = initialQuery;
        
        // Get all registered tables
        const tables = await engine.listRegisteredTables();
        
        if (tables.length === 0) {
          console.warn('[NovaSimplified] No tables available in engine');
          return;
        }
        
        console.log(`[NovaSimplified] Available tables: ${tables.length}`,
          tables.map(t => t.path));
        
        // Try exact match first (case insensitive)
        let tableToActivate = tables.find(
          table => table.path?.toLowerCase() === initialQuery.toLowerCase()
        );
        
        // If not found by exact match, try to find partial match
        if (!tableToActivate) {
          // Try partial match
          tableToActivate = tables.find(
            table => table.path?.toLowerCase().includes(initialQuery.toLowerCase())
          );
          
          if (tableToActivate) {
            console.log(`[NovaSimplified] Found table by partial match: ${tableToActivate.path}`);
          }
        } else {
          console.log(`[NovaSimplified] Found table by exact match: ${tableToActivate.path}`);
        }
        
        if (tableToActivate) {
          console.log(`[NovaSimplified] Activating table: ${tableToActivate.path} (id: ${tableToActivate.id})`);
          // Set the active table
          await setActiveTable(tableToActivate);
        } else {
          console.warn(`[NovaSimplified] Table not found: "${initialQuery}". Available tables:`, 
            tables.map(t => t.path).join(', '));
        }
      } catch (err) {
        console.error('[NovaSimplified] Error setting active table:', err);
      }
    };
    
    // Run the table change handler
    handleTableChange();
  }, [initialQuery, engine, setActiveTable, isLoading]);
  
  // Simplified loading indicator logic - show loading whenever status is 'loading'
  React.useEffect(() => {
    if (isLoading) {
      // Show loading indicator immediately for any loading state
      setShowLoadingIndicator(true);
    } else {
      // Hide loading indicator when not loading
      setShowLoadingIndicator(false);
    }
  }, [isLoading]);
  
  // Log the current state for debugging
  React.useEffect(() => {
    logState();
  }, [logState]);
  
  // Check if we should show loading skeleton - show it when we have no schema OR no data
  const shouldShowLoadingSkeleton = React.useMemo(() => {
    // Show loading skeleton if we don't have the basic schema yet OR if we don't have data yet
    return originalColumnRefs.length === 0 || !data;
  }, [originalColumnRefs.length, data]);

  // Render the Nova container with DataGrid without the Datasets Sidebar
  return (
    <div className={cn("w-full h-full", className)}>
      <ResizablePanelGroup 
        direction="horizontal" 
        className="h-full w-full border rounded-md overflow-hidden"
      >
        {/* Main Content Panel */}
        <ResizablePanel defaultSize={65} minSize={30} className="h-full">
          <div className="h-full w-full" ref={containerRef}>
            {shouldShowLoadingSkeleton ? (
              <div className="h-full w-full flex flex-col">
                {/* Header skeleton */}
                <div className="shrink-0 border-b bg-background px-2 py-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="h-5 w-40 bg-muted animate-pulse rounded" />
                      <div className="h-4 w-24 bg-muted animate-pulse rounded" />
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="h-8 w-20 bg-muted animate-pulse rounded" />
                      <div className="h-8 w-20 bg-muted animate-pulse rounded" />
                      <div className="h-8 w-16 bg-muted animate-pulse rounded" />
                    </div>
                  </div>
                </div>
                
                {/* Data grid skeleton area */}
                <div className="flex-1 overflow-hidden p-4">
                  <div className="h-full space-y-3">
                    {/* Column headers skeleton */}
                    <div className="flex gap-3 border-b pb-3">
                      {Array(8).fill(0).map((_, i) => (
                        <div key={`header-${i}`} className="h-9 flex-1 min-w-0 bg-muted animate-pulse rounded" />
                      ))}
                    </div>
                    
                    {/* Rows skeleton */}
                    {Array(12).fill(0).map((_, rowIndex) => (
                      <div key={`row-${rowIndex}`} className="flex gap-3 py-2">
                        {Array(8).fill(0).map((_, colIndex) => (
                          <div 
                            key={`cell-${rowIndex}-${colIndex}`} 
                            className="h-7 flex-1 min-w-0 bg-muted animate-pulse rounded" 
                            style={{ 
                              opacity: Math.max(0.3, 1 - (rowIndex * 0.04)) // Better fade
                            }} 
                          />
                        ))}
                      </div>
                    ))}
                    
                    {/* Footer area skeleton */}
                    <div className="flex justify-between items-center pt-4 border-t">
                      <div className="h-6 w-32 bg-muted animate-pulse rounded" />
                      <div className="flex gap-2">
                        <div className="h-8 w-16 bg-muted animate-pulse rounded" />
                        <div className="h-8 w-16 bg-muted animate-pulse rounded" />
                        <div className="h-8 w-20 bg-muted animate-pulse rounded" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : originalColumnRefs.length > 0 && safeColumnRefs.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full w-full p-8 text-center">
                <div className="mb-4 text-muted-foreground">
                  <BarChart className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <h3 className="text-lg font-medium">Query Result Has No Columns</h3>
                </div>
                <p className="max-w-md text-sm text-muted-foreground mb-4">
                  The table schema was loaded successfully, but the current query didn't return any columns.
                  This could be due to an issue with the query or the data.
                </p>
                <div className="text-sm text-muted-foreground mb-4">
                  <p><strong>Original Columns:</strong> {originalColumnRefs.length}</p>
                  <p><strong>Current Columns:</strong> {safeColumnRefs.length}</p>
                  <p><strong>Data Status:</strong> {status}</p>
                </div>
                <button 
                  className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
                  onClick={() => {
                    // Create a simple SELECT * query using original columns
                    const fixQuery = {
                      type: QueryType.SELECT,
                      options: {
                        columns: originalColumnRefs,
                        where: "",
                        orderBy: []
                      }
                    };
                    console.log('Running recovery query with all columns');
                    executeQuery(fixQuery);
                  }}
                >
                  Show All Columns
                </button>
              </div>
            ) : originalColumnRefs.length > 0 && safeColumnRefs.length > 0 ? (
              <NovaContainer
                query={query}
                columns={safeColumnRefs}
                originalColumns={originalColumnRefs}
                updateQuery={updateQuery}
                executeQuery={() => executeQuery(query)}
                dataName={tableName}
                rowCount={data?.rowCount || 0}
                className="h-full w-full"
                topRightSlot={topRightSlot}
                lastExecutedView={lastExecutedView}
              >
                {data && (
                  <div className="h-full w-full">
                    <DataGrid
                      data={data}
                      columns={convertToColumns(safeColumnRefs)}
                      loading={isLoading || showLoadingIndicator}
                      error={error}
                      className="h-full w-full border-0 shadow-none"
                      width="100%"
                      query={lastExecutedQuery || query}
                      lastExecutedQuery={lastExecutedQuery}
                      updateQuery={updateQuery}
                      executeQuery={executeQuery}
                    />
                  </div>
                )}
              </NovaContainer>
            ) : (
              <div className="flex flex-col items-center justify-center h-full w-full p-8 text-center">
                <div className="mb-4 text-muted-foreground">
                  <BarChart className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <h3 className="text-lg font-medium">No Schema Available</h3>
                </div>
                <p className="max-w-md text-sm text-muted-foreground mb-4">
                  Unable to load table schema. Please check that the table is properly registered.
                </p>
                <div className="text-sm text-muted-foreground">
                  <p><strong>Active Table:</strong> {activeTable?.path || 'None'}</p>
                  <p><strong>Status:</strong> {status}</p>
                  <p><strong>Error:</strong> {error?.message || 'None'}</p>
                </div>
              </div>
            )}
          </div>
        </ResizablePanel>

        {/* Resizable Handle for AI Chat Panel */}
        <ResizableHandle withHandle />

        {/* Nova AI Chat Sidebar */}
        <ResizablePanel defaultSize={35} minSize={12} maxSize={70} className="h-full border-l">
          <div className="h-full w-full overflow-hidden">
            <NovaAIChat 
              className="h-full w-full"
              chatEndpoint={chatEndpoint}
              engine={engine}
              onRunSqlQuery={runSqlQuery}
              subscriptionInfo={subscriptionInfo}
              onUpgradeClick={onUpgradeClick}
              initialPrompt={initialPrompt}
            />
          </div>
        </ResizablePanel>
      </ResizablePanelGroup>
    </div>
  );
}

// Re-export the store for direct use in other components
export { useNovaStore } from './nova/nova-store';
export type { NovaStore } from './nova/nova-store'; 

/**
 * Example usage pattern for NovaSimplified
 * 
 * This demonstrates the correct way to initialize the engine, register a table,
 * and render the NovaSimplified component. Copy and adapt this to your needs.
 * 
 * @example
 * ```tsx
 * import { createExampleNovaExplorer } from '@/components/nova/nova-simplified';
 * 
 * export default function DataExplorerPage() {
 *   return (
 *     <div className="h-screen">
 *       {createExampleNovaExplorer({
 *         csvPath: '/data/my-data.csv', 
 *         tableName: 'my_table'
 *       })}
 *     </div>
 *   );
 * }
 * ```
 */
export function createExampleNovaExplorer({ 
  csvPath = '/data/sample.csv', 
  tableName = 'example_table' 
}: { 
  csvPath?: string; 
  tableName?: string 
}) {
  // This is a template function that demonstrates proper usage
  return (
    <div className="relative flex items-center justify-center h-full">
      <div className="p-6 max-w-md rounded bg-slate-100 shadow-lg">
        <h3 className="text-lg font-semibold mb-3">NovaSimplified Usage Template</h3>
        <p className="text-sm mb-3">
          This is a template showing the correct pattern for using NovaSimplified.
          Copy this code into your application and customize as needed.
        </p>
        <pre className="text-xs bg-slate-200 p-3 rounded overflow-auto">
{`// 1. Import the required components
import { useEffect, useState } from 'react';
import { NovaSimplified } from '@/components/nova/nova-simplified';
import { useEngine } from '@/lib/engine/react';
import { useSupabaseClient } from '@supabase/auth-helpers-react';

export function MyDataExplorer() {
  // 2. Get the engine and Supabase clients
  const engine = useEngine();
  const supabase = useSupabaseClient();
  const [isReady, setIsReady] = useState(false);
  
  // 3. Initialize the engine and register tables
  useEffect(() => {
    async function setupEngine() {
      // Wait for engine to be ready
      await engine.waitForInitialization();
      
      // Register a table (from CSV, Parquet, etc.)
      const response = await fetch('${csvPath}');
      const blob = await response.blob();
      await engine.registerTable({
        id: 'unique-id',
        path: '${tableName}', // This is the value for initialQuery
        file: blob,
        fileType: 'csv'
      });
      
      setIsReady(true);
    }
    
    setupEngine().catch(console.error);
  }, [engine]);
  
  // 4. Show loading state until ready
  if (!isReady) {
    return <div>Loading data...</div>;
  }
  
  // 5. Render NovaSimplified with the engine
  return (
    <div className="h-screen">
      <NovaSimplified 
        engine={engine} 
        supabase={supabase}
        initialQuery="${tableName}"
      />
    </div>
  );
}`}
        </pre>
      </div>
    </div>
  );
} 